import { Controller } from "@hotwired/stimulus";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MIN_IMAGE_DIMENSION = 200; // 200px
const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
];

export default class extends Controller {
  static targets = [
    "fileInput",
    "cameraInput",
    "uploadArea",
    "cameraButton",
    "cameraView",
    "video",
    "previewArea",
    "previewIcon",
    "previewImage",
    "fileName",
    "fileSize",
    "submitButton",
    "errorMessage",
    "bottomActions",
    "dataUriInput",
  ];

  disconnect() {
    this.stopCameraStream();
  }

  selectFile() {
    this.fileInputTarget.click();
  }

  capturePhoto() {
    this.clearError();

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.cameraInputTarget.click();
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => {
        this.stream = stream;
        this.videoTarget.srcObject = stream;
        this.uploadAreaTarget.classList.add("hidden");
        this.cameraButtonTarget.classList.add("hidden");
        this.bottomActionsTarget.classList.add("hidden");
        this.cameraViewTarget.classList.remove("hidden");
      })
      .catch(() => {
        this.cameraInputTarget.click();
      });
  }

  takePicture() {
    const video = this.videoTarget;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);

    this.stopCameraStream();
    this.cameraViewTarget.classList.add("hidden");

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          this.showError("Could not capture photo. Please try again.");
          return;
        }
        const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
        this.processFile(file);
      },
      "image/jpeg",
      0.92,
    );
  }

  cancelCamera() {
    this.stopCameraStream();
    this.cameraViewTarget.classList.add("hidden");
    this.uploadAreaTarget.classList.remove("hidden");
    this.cameraButtonTarget.classList.remove("hidden");
    this.bottomActionsTarget.classList.remove("hidden");
  }

  stopCameraStream() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
  }

  handleFileSelected(event) {
    const file = event.target.files[0];
    if (!file) return;
    this.processFile(file);
  }

  handleCameraCapture(event) {
    const file = event.target.files[0];
    if (!file) return;
    this.processFile(file);
  }

  processFile(file) {
    this.clearError();

    if (!ALLOWED_TYPES.includes(file.type)) {
      this.showError("Please select a PDF, JPEG, or PNG file.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      this.showError("File must be smaller than 5MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUri = e.target.result;

      if (file.type.startsWith("image/")) {
        this.validateImageAndShow(file, dataUri);
      } else {
        this.dataUriInputTarget.value = dataUri;
        this.showFilePreview(file, null);
      }
    };
    reader.onerror = () => {
      this.showError("Could not read file. Please try again.");
    };
    reader.readAsDataURL(file);
  }

  validateImageAndShow(file, dataUri) {
    const img = new Image();
    img.onload = () => {
      if (
        img.width < MIN_IMAGE_DIMENSION ||
        img.height < MIN_IMAGE_DIMENSION
      ) {
        this.showError(
          `Image must be at least ${MIN_IMAGE_DIMENSION}x${MIN_IMAGE_DIMENSION} pixels.`,
        );
        return;
      }
      this.dataUriInputTarget.value = dataUri;
      this.showFilePreview(file, dataUri);
    };
    img.onerror = () => {
      this.showError("Could not read image. Please try another file.");
    };
    img.src = dataUri;
  }

  showFilePreview(file, dataUrl) {
    this.fileNameTarget.textContent = file.name;
    this.fileSizeTarget.textContent = this.formatFileSize(file.size);

    if (dataUrl && file.type.startsWith("image/")) {
      this.previewImageTarget.src = dataUrl;
      this.previewImageTarget.classList.remove("hidden");
    } else {
      this.previewImageTarget.classList.add("hidden");
    }

    this.uploadAreaTarget.classList.add("hidden");
    this.cameraButtonTarget.classList.add("hidden");
    this.bottomActionsTarget.classList.remove("hidden");
    this.previewAreaTarget.classList.remove("hidden");
    this.submitButtonTarget.removeAttribute("disabled");
  }

  removeFile() {
    this.fileInputTarget.value = "";
    this.cameraInputTarget.value = "";
    this.previewImageTarget.src = "";
    this.dataUriInputTarget.value = "";

    this.previewAreaTarget.classList.add("hidden");
    this.uploadAreaTarget.classList.remove("hidden");
    this.cameraButtonTarget.classList.remove("hidden");
    this.submitButtonTarget.setAttribute("disabled", "disabled");
    this.clearError();
  }

  showError(message) {
    this.errorMessageTarget.textContent = message;
    this.errorMessageTarget.classList.remove("hidden");
    this.fileInputTarget.value = "";
    this.cameraInputTarget.value = "";
    this.dataUriInputTarget.value = "";
    this.uploadAreaTarget.classList.remove("hidden");
    this.cameraButtonTarget.classList.remove("hidden");
    this.bottomActionsTarget.classList.remove("hidden");
  }

  clearError() {
    this.errorMessageTarget.textContent = "";
    this.errorMessageTarget.classList.add("hidden");
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
